import { FC } from "react";
import { useSelector } from "react-redux";
import { Container, GridItem, GridWrapper } from "@sainsburys-tech/luna-grid";
import { TextInputField, SelectField, Form } from "@sainsburys-tech/luna-form";
import { CaseHeightUOMOptions, CaseLengthUOMOptions, CaseWeightUOMOptions, CaseWidthUOMOptions, FIELDS, FormInputLevels, VolumetricFormInputs } from "../../../Interfaces/Product/IProductFormField";
import { Heading6 } from "@sainsburys-tech/luna-typography";
import ActionButton from "../../SharedComponents/ActionButton";
import { FoodVolumetricsReviewError, FoodVolumetricsReviewWarning } from "../../../Interfaces/IFoodVolumetricsReviewType";
import { FoodVolumetricsFormProps } from "../../../Types/Product/FoodVolumetricsFormProps";
import { VolumetricsReviewDataModel } from "../../../Types/Product/VolumetricsReviewDataModel";
import ApprovedExceptionCheckbox from "../../SharedComponents/ApprovedExceptionCheckbox";

const FoodVolumetricsForm: FC<FoodVolumetricsFormProps> = (props) => {
    const errros = props.errors as FoodVolumetricsReviewError;
    const warnings = props.warnings as FoodVolumetricsReviewWarning;
    const product: VolumetricsReviewDataModel = props.values;
    const payload = useSelector((state: any) => state.popupReducer);

    const numToStringReplacer = (key: any, value: any) => {
        if (typeof value === "number") {
            return value?.toString();
        }
        return value;
    };

    const productNotExistInLDM = product.rejectionReason?.toLowerCase().trim() === "the product with the specified pack size does not exist in ldm.";
    const isInitialState = !productNotExistInLDM && JSON.stringify(payload?.data?.editPayload?.values, numToStringReplacer) === JSON.stringify(props.values, numToStringReplacer);

    return (
        <Container>
            <Form className="o-form">
                <GridWrapper equalHeight className="ln-u-push-bottom">
                    <GridItem size={{ sm: "1/3", xs: "1", }}>
                        <TextInputField
                            name={FIELDS.skuNumber.name}
                            label={FormInputLevels.SkuNumber}
                            value={product.skuNo}
                            disabled
                        />
                    </GridItem>
                    <GridItem size={{ sm: "1/3", xs: "1" }}>
                        <TextInputField
                            name={FIELDS.superCatDesc.name}
                            label={FormInputLevels.SuperCatDesc}
                            value={product.superCatDesc}
                            disabled
                        />
                    </GridItem>
                    <GridItem size={{ sm: "1/3", xs: "1" }}>
                        <TextInputField
                            name={FIELDS.description.name}
                            label={FormInputLevels.Description}
                            value={product.desc}
                            disabled
                        />
                    </GridItem>
                </GridWrapper>

                <>
                    <Heading6 element="div">Dimensions</Heading6>
                    <GridWrapper className="ln-u-push-bottom">
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <TextInputField
                                name={FIELDS.packSize.name}
                                label={FormInputLevels.PackSizeLabel}
                                value={product.packSize}
                                type="number"
                                disabled
                            />
                        </GridItem>
                    </GridWrapper>

                    <GridWrapper className="ln-u-push-bottom">
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <TextInputField
                                name={'caseLengthMm'}
                                label={VolumetricFormInputs.CaseLength}
                                value={product.caseLengthMm}
                                error={errros.caseLengthMm}
                                warning={errros.caseLengthMm ? "" : warnings?.caseLengthMm}
                                onBlur={props.onBlur}
                                onChange={(e: any) => {
                                    props.onChange(e, "", "", false);
                                }}
                                type="number"
                                required
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <SelectField
                                name={FIELDS.innerDimensions.lengthUOM.name}
                                label={FormInputLevels.InnerLengthUOM}
                                options={CaseLengthUOMOptions}
                                defaultValue={CaseLengthUOMOptions[0].value}
                                onChange={props.onChange}
                                onBlur={props.onBlur}
                                placeholder={false}
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <TextInputField
                                name={'caseWidthMm'}
                                label={VolumetricFormInputs.CaseWidth}
                                value={product.caseWidthMm}
                                error={errros.caseWidthMm}
                                warning={errros.caseWidthMm ? "" : warnings?.caseWidthMm}
                                onBlur={props.onBlur}
                                onChange={(e: any) => {
                                    props.onChange(e, "", "", false);
                                }}
                                type="number"
                                required
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <SelectField
                                name={FIELDS.innerDimensions.widthUOM.name}
                                label={FormInputLevels.InnerWidthUOM}
                                options={CaseWidthUOMOptions}
                                defaultValue={CaseWidthUOMOptions[0].value}
                                onChange={props.onChange}
                                onBlur={props.onBlur}
                                placeholder={false}
                            />
                        </GridItem>
                    </GridWrapper>

                    <GridWrapper className="ln-u-push-bottom">
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <TextInputField
                                name={'caseHeightMm'}
                                label={VolumetricFormInputs.CaseHeight}
                                value={product.caseHeightMm}
                                error={errros.caseHeightMm}
                                onBlur={props.onBlur}
                                onChange={(e: any) => {
                                    props.onChange(e, "", "", false);
                                }}
                                type="number"
                                required
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <SelectField
                                name={FIELDS.innerDimensions.heightUOM.name}
                                label={FormInputLevels.InnerHeightUOM}
                                options={CaseHeightUOMOptions}
                                defaultValue={CaseHeightUOMOptions[0].value}
                                onChange={props.onChange}
                                onBlur={props.onBlur}
                                placeholder={false}
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <TextInputField
                                name={'caseWeightKg'}
                                label={VolumetricFormInputs.CaseWeight}
                                value={product.caseWeightKg}
                                error={errros.caseWeightKg}
                                onBlur={props.onBlur}
                                onChange={(e: any) => {
                                    props.onChange(e, "", "", false);
                                }}
                                type="number"
                                required
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/4", xs: "1" }}>
                            <SelectField
                                name={FIELDS.innerDimensions.weightUOM.name}
                                label={FormInputLevels.InnerWeightUOM}
                                options={CaseWeightUOMOptions}
                                defaultValue={CaseWeightUOMOptions[0].value}
                                onChange={props.onChange}
                                onBlur={props.onBlur}
                                placeholder={false}
                            />
                        </GridItem>
                    </GridWrapper>

                    <GridWrapper className="ln-u-push-bottom">
                        <GridItem size={{ sm: "1/3", xs: "1" }}>
                            <TextInputField
                                name={'casesPerLayer'}
                                label={VolumetricFormInputs.CasesPerLayer}
                                value={product.casesPerLayer}
                                error={errros.casesPerLayer}
                                onBlur={props.onBlur}
                                onChange={(e: any) => {
                                    props.onChange(e, "", "", false);
                                }}
                                type="number"
                                required
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/3", xs: "1" }}>
                            <TextInputField
                                name={'layersPerPallet'}
                                label={VolumetricFormInputs.LayersPerPallet}
                                value={product.layersPerPallet}
                                error={errros.layersPerPallet}
                                onBlur={props.onBlur}
                                onChange={(e: any) => {
                                    props.onChange(e, "", "", false);
                                }}
                                type="number"
                                required
                            />
                        </GridItem>
                        <GridItem size={{ sm: "1/3", xs: "1" }}>
                            <TextInputField
                                name={'casesPerPallet'}
                                label={VolumetricFormInputs.CasesPerPallet}
                                value={(product?.casesPerLayer && product?.layersPerPallet) ? (product.casesPerLayer * product.layersPerPallet) : 0}
                                error={errros.casesPerPallet}
                                disabled
                            />
                        </GridItem>
                    </GridWrapper>
                </>
            </Form>

            <GridWrapper className="ln-u-push-bottom">
                <GridItem size={{ sm: "1/3", xs: "1" }}>
                    <ApprovedExceptionCheckbox
                        checked={product.overridenValidations}
                        onChange={props.onChange}
                    />
                </GridItem>
            </GridWrapper>

            <ActionButton
                onSubmit={props.onSubmit}
                onClearAll={props.onClearAll}
                onCancel={props.onCancel}
                isAddDisabled={isInitialState}
                isDirty={props.dirty}
                isEdit={props.isEdit}
                isClearButtonRequired={false}
                submitted={props.submitted}
            />
        </Container>
    );
}

export default FoodVolumetricsForm;